import classNames from "classnames";
import { FC, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AirQualityCard, { AirQualityCardData } from "modules/assets/AirQualityCard";
import ThermalCard, { ThermalCardData } from "modules/assets/ThermalCard";

import {
  HumidityCard as HumidityCardType,
  HumidityStatus,
  MetricCard as MetricCardType,
  MetricStatus,
  ThermalCard as ThermalCardType,
  ThermalStatus,
} from "entities/assets";
import Tag, { TagTone } from "modules/assets/Tag";
import { PAGES } from "utils/links";
import styles from "./style.module.scss";

// export type AssetGroupCard = { type: "air"; data: AirQualityCardData } | { type: "thermal"; data: ThermalCardData };
export type AssetGroupCard = {
  metric: "co2" | "hcho" | "tvoc" | "pm25" | "pm10" | "temperature" | "humidity";
  kind: "metric" | "thermal" | "humidity" | "air";
  status: HumidityStatus | ThermalStatus | MetricStatus;
  metric_card: MetricCardType | null;
  thermal_card: ThermalCardType | null;
  humidity_card: HumidityCardType | null;
  data: unknown;
};

export type AssetGroupData = {
  id: string;
  name: string;
  overallStatus: string;
  status: { tone: TagTone; label: string };
  cards?: AssetGroupCard[];
  emptyMessage?: string;
  image?: string;
};

type PropTypes = {
  data: AssetGroupData;
  className?: string;
};

const AssetGroup: FC<PropTypes> = ({ data, className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasCards = Boolean(data.cards?.length);

  const handleViewDetails = () => {
    navigate(PAGES.ASSET_DETAIL.replace(":id", data.id), {
      state: { returnTo: location.pathname + location.search },
    });
  };

  return (
    <section className={classNames(styles.group, styles[`tone-${data.status.tone}`], className)}>
      <header className={styles.head}>
        {data?.image ? (
          <img src={data.image} alt={data.name} className={styles.avatar} />
        ) : (
          <span className={classNames(styles.avatar, styles.avatarFallback)} aria-hidden>
            {(data.name || "?").charAt(0).toUpperCase()}
          </span>
        )}

        <span className={styles.name}>{data.name}</span>
        <Tag tone={data.status.tone}>{data.status.label}</Tag>
      </header>

      {hasCards ? (
        <div className={styles.cards}>
          {data.cards?.map((card, index) => {
            const to = PAGES.ASSET_PARAMETER_BREAKDOWN.replace(":id", data.id).replace(":parameter", card.metric);

            return card?.kind === "metric" ? (
              <AirQualityCard key={`${data.id}-air-${index}`} data={card.data as AirQualityCardData} to={to} />
            ) : (
              <ThermalCard key={`${data.id}-thermal-${index}`} data={card.data as ThermalCardData} to={to} />
            );
          })}
        </div>
      ) : (
        <p className={styles.empty}>
          {data.emptyMessage ?? (data.overallStatus === "no_data" ? "No data available" : "All metrics within limits")}
        </p>
      )}

      <button type="button" className={styles.viewDetails} onClick={handleViewDetails}>
        View full asset details
      </button>
    </section>
  );
};

export default memo(AssetGroup);
